// The seats picked on the seat blocking page's grid (IDV-705), with the seat editor's selection model: click, Ctrl/Cmd
// to add or remove one, Shift for the rectangle from the last anchor, a dragged box, a whole Row. Only available and
// blocked seats get in; reserved and sold ones are left out and counted.
import { computed, reactive, ref, type Ref } from 'vue'
import { SeatStatus, SeatStatusHints, SeatStatusLabels, isChangeable } from '@/constants/SeatStatuses'
import type { EventSeat } from '@/types/EventSeats'
import type { BoardRow, BoardSection, Notice, SeatBoard } from './board'

/** What the panel's buttons do: block the selection's available seats, or unblock its blocked ones */
export type SeatAction = 'block' | 'unblock'

/** A cell of the open Section's grid */
export interface CellRef {
  rowId: string
  col: number
}

export interface ClickModifiers {
  /** Shift: the rectangle from the anchor */
  shift?: boolean
  /** Ctrl/Cmd: add or remove one seat */
  toggle?: boolean
}

interface Box {
  start: CellRef
  end: CellRef
  moved: boolean
  /** The selection kept under an additive (Ctrl/Cmd) box */
  base: string[]
}

export function useSeatSelection(board: SeatBoard, sectionId: () => string | null) {
  const ids: Ref<string[]> = ref([])
  const anchor: Ref<CellRef | null> = ref(null)
  const box: Ref<Box | null> = ref(null)
  /** Sold or reserved seats the last box, rectangle or Row left out */
  const skipped: Ref<number> = ref(0)

  const section = computed<BoardSection | undefined>(() => {
    const id = sectionId()
    return id ? board.section(id) : undefined
  })

  const selected = computed<Set<string>>(() => new Set(ids.value))

  function withStatus(status: SeatStatus): string[] {
    return ids.value.filter(id => board.statusOf(id) === status)
  }

  function rowIndex(rowId: string): number {
    return section.value?.rows.findIndex(row => row.id === rowId) ?? -1
  }

  function seatAt(cell: CellRef): EventSeat | undefined {
    return section.value?.rows[rowIndex(cell.rowId)]?.seats.find(seat => seat.position === cell.col)
  }

  /** The seats inside the rectangle between two cells, Row by Row */
  function seatsBetween(a: CellRef, b: CellRef): EventSeat[] {
    const [i0, i1] = [rowIndex(a.rowId), rowIndex(b.rowId)]
    if (i0 < 0 || i1 < 0) return []

    const [c0, c1] = [Math.min(a.col, b.col), Math.max(a.col, b.col)]
    const rows: BoardRow[] = section.value?.rows.slice(Math.min(i0, i1), Math.max(i0, i1) + 1) ?? []
    return rows.flatMap(row => row.seats.filter(seat => seat.position >= c0 && seat.position <= c1))
  }

  /** The seats that can change, counting the others as skipped */
  function pick(seats: EventSeat[]): string[] {
    const changeable = seats.filter(seat => isChangeable(seat.status)).map(seat => seat.id)
    skipped.value = seats.length - changeable.length
    return changeable
  }

  function union(base: string[], extra: string[]): string[] {
    const kept = new Set(base)
    return [...base, ...extra.filter(id => !kept.has(id))]
  }

  function clear() {
    ids.value = []
    anchor.value = null
    skipped.value = 0
  }

  /** Selects what was clicked; a sold or reserved seat isn't selected, and the notice says why */
  function click(cell: CellRef, { shift = false, toggle = false }: ClickModifiers = {}): Notice | null {
    if (shift && anchor.value) {
      ids.value = pick(seatsBetween(anchor.value, cell))
      return null
    }

    const seat = seatAt(cell)
    if (!seat) {
      if (!toggle) clear()
      return null
    }

    if (!isChangeable(seat.status)) {
      return { text: `${seat.full_label}: ${SeatStatusLabels[seat.status]}. ${SeatStatusHints[seat.status]}`, kind: 'info' }
    }

    skipped.value = 0
    anchor.value = cell
    if (!toggle) ids.value = [ seat.id ]
    else if (selected.value.has(seat.id)) ids.value = ids.value.filter(id => id !== seat.id)
    else ids.value = [...ids.value, seat.id]

    return null
  }

  // Box -------------------------------------------------------------------------------------------------------------

  /** Starts a box; the selection only changes once the drag reaches another cell */
  function press(cell: CellRef, additive = false) {
    box.value = { start: cell, end: cell, moved: false, base: additive ? [ ...ids.value ] : [] }
  }

  function drag(cell: CellRef) {
    const b = box.value
    if (!b) return

    b.end = cell
    if (b.start.rowId !== cell.rowId || b.start.col !== cell.col) b.moved = true
    if (b.moved) ids.value = union(b.base, pick(seatsBetween(b.start, b.end)))
  }

  /** Ends the box; true when it was dragged, so the click that follows the release is ignored */
  function release(): boolean {
    const b = box.value
    box.value = null
    if (b?.moved) anchor.value = b.start
    return !!b?.moved
  }

  function inBox(cell: CellRef): boolean {
    const b = box.value
    if (!b?.moved) return false

    const i = rowIndex(cell.rowId)
    const [i0, i1] = [rowIndex(b.start.rowId), rowIndex(b.end.rowId)]
    return i >= Math.min(i0, i1) && i <= Math.max(i0, i1)
      && cell.col >= Math.min(b.start.col, b.end.col) && cell.col <= Math.max(b.start.col, b.end.col)
  }

  // Groups ----------------------------------------------------------------------------------------------------------

  function selectRow(rowId: string, additive = false) {
    const row = section.value?.rows[rowIndex(rowId)]
    if (!row) return

    const picked = pick(row.seats)
    ids.value = additive ? union(ids.value, picked) : picked
    anchor.value = null
  }

  /** Every seat of the open Section in this status, as "Selecionar os N bloqueados" does */
  function selectStatus(status: SeatStatus) {
    ids.value = section.value?.rows.flatMap(row => row.seats.filter(seat => seat.status === status).map(seat => seat.id)) ?? []
    anchor.value = null
    skipped.value = 0
  }

  // After a reload --------------------------------------------------------------------------------------------------

  /** Drops the seats that can no longer change (sold or reserved since); returns their labels */
  function prune(): string[] {
    const dropped = ids.value.filter((id) => {
      const status = board.statusOf(id)
      return !status || !isChangeable(status)
    })
    remove(dropped)

    return dropped.map(board.label).filter(Boolean)
  }

  function remove(seatIds: string[]) {
    const gone = new Set(seatIds)
    ids.value = ids.value.filter(id => !gone.has(id))
  }

  return reactive({
    ids,
    skipped,
    dragging: computed<boolean>(() => box.value !== null),
    available: computed<string[]>(() => withStatus(SeatStatus.AVAILABLE)),
    blocked: computed<string[]>(() => withStatus(SeatStatus.BLOCKED)),
    has: (id: string): boolean => selected.value.has(id),
    /** The selected seats a button sends: only those in the status it starts from */
    seatsFor: (action: SeatAction): string[] => withStatus(action === 'block' ? SeatStatus.AVAILABLE : SeatStatus.BLOCKED),
    clear,
    click,
    press,
    drag,
    release,
    inBox,
    selectRow,
    selectStatus,
    prune,
    remove,
  })
}

export type SeatSelection = ReturnType<typeof useSeatSelection>
